import React from "react";
import { CheckCircle2, Info, TriangleAlert, XCircle } from "lucide-react";
import Modal from "./Modal";
import { useUiMessages } from "./UiMessages";

export type AlertDialogType = "warning" | "danger" | "info" | "success";

export interface AlertDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  type?: AlertDialogType;
  confirmText?: string;
  closeLabel?: string;
  onConfirm: () => void;
}

const CONFIGS: Record<AlertDialogType, { icon: React.ComponentType<{ className?: string }>; iconColor: string; btnColor: string }> = {
  danger:  { icon: XCircle,       iconColor: "text-danger",  btnColor: "bg-danger-strong hover:bg-danger-strong-hover" },
  success: { icon: CheckCircle2,  iconColor: "text-success", btnColor: "bg-action-primary hover:bg-action-primary-hover" },
  warning: { icon: TriangleAlert, iconColor: "text-warning", btnColor: "bg-action-primary hover:bg-action-primary-hover" },
  info:    { icon: Info,          iconColor: "text-info",    btnColor: "bg-action-primary hover:bg-action-primary-hover" },
};

export const AlertDialog: React.FC<AlertDialogProps> = ({
  isOpen, title, message, type = "info", confirmText, closeLabel, onConfirm,
}) => {
  const { dialog } = useUiMessages();
  const { icon: Icon, iconColor, btnColor } = CONFIGS[type];
  return (
    <Modal
      isOpen={isOpen}
      onClose={onConfirm}
      contentClassName="p-6"
      showCloseButton
      closeLabel={closeLabel ?? dialog.closeAlert}
      fallbackTitle={title}
    >
      <div className="flex items-start gap-4">
        <Icon className={`h-6 w-6 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1">
          <h2 className="text-lg font-medium text-text-primary">{title}</h2>
          <p className="mt-2 text-sm text-text-muted">{message}</p>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onConfirm}
          className={`cursor-pointer rounded-lg px-4 py-2 text-sm font-medium text-white ${btnColor}`}
        >
          {confirmText ?? dialog.confirm}
        </button>
      </div>
    </Modal>
  );
};

export default AlertDialog;
